import { ReactNode, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

type HoverTooltipProps = {
  text: string;
  children: ReactNode;
  className?: string;
  /** Texto longo, posicionado fora do card para não ser cortado. */
  multiline?: boolean;
};

const MULTILINE_WIDTH = 288;
const VIEWPORT_GAP = 8;

export function HoverTooltip({ text, children, className = '', multiline = false }: HoverTooltipProps) {
  if (multiline) {
    return (
      <MultilineHoverTooltip text={text} className={className}>
        {children}
      </MultilineHoverTooltip>
    );
  }

  return (
    <span className={`group relative inline-flex ${className}`}>
      {children}
      <span
        role="tooltip"
        className="pointer-events-none absolute bottom-[calc(100%+0.4rem)] left-1/2 z-[1100] -translate-x-1/2 whitespace-nowrap rounded-md bg-secondary-900 px-2 py-1 text-[11px] font-medium text-white opacity-0 shadow-sm transition-opacity delay-75 group-hover:opacity-100 group-focus-within:opacity-100"
      >
        {text}
      </span>
    </span>
  );
}

function MultilineHoverTooltip({ text, children, className = '' }: HoverTooltipProps) {
  const anchorRef = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, above: true });

  function show(): void {
    const rect = anchorRef.current?.getBoundingClientRect();
    if (!rect) return;

    const left = Math.max(
      VIEWPORT_GAP,
      Math.min(rect.left, window.innerWidth - MULTILINE_WIDTH - VIEWPORT_GAP),
    );
    const above = rect.top > 96;
    setPosition({
      top: above ? rect.top - VIEWPORT_GAP : rect.bottom + VIEWPORT_GAP,
      left,
      above,
    });
    setOpen(true);
  }

  return (
    <span
      ref={anchorRef}
      className={`inline-flex ${className}`}
      onMouseEnter={show}
      onMouseLeave={() => setOpen(false)}
      onFocus={show}
      onBlur={() => setOpen(false)}
    >
      {children}
      {open
        ? createPortal(
            <span
              role="tooltip"
              className={`pointer-events-none fixed z-[1200] w-72 rounded-md bg-secondary-900 px-2.5 py-1.5 text-left text-xs font-normal leading-snug text-white shadow-sm ${
                position.above ? '-translate-y-full' : ''
              }`}
              style={{ top: position.top, left: position.left }}
            >
              {text}
            </span>,
            document.body,
          )
        : null}
    </span>
  );
}
